import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FiBell, FiPlus, FiAlertCircle, FiX, FiSend, FiLoader } from "react-icons/fi";
import { workshopApi } from "../../services/workshopApi";
import "./Announcements.css";

// Animation variants matching WorkshopDetail
const itemVariants = {
  initial: { opacity: 0, y: 16 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.4, ease: [0.25, 0.46, 0.45, 0.94] } },
};

const Announcements = ({ 
  workshopId, 
  userRole, 
  onNavigateToOverview,
  canSendAnnouncements: canSendAnnouncementsProp // Permission prop from parent for co-organizers
}) => {
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [userPermissions, setUserPermissions] = useState(null);

  // Use API permissions (userPermissions.canCreate) if available, otherwise fall back to prop-based logic
  // Organizers can always create, co-organizers need canSendAnnouncements permission
  const canCreateAnnouncements =
    userPermissions?.canCreate ?? (
      userRole === "organizer" || 
      (userRole === "co-organizer" && canSendAnnouncementsProp !== false)
    );

  useEffect(() => {
    fetchAnnouncements();
  }, [workshopId]);

  const fetchAnnouncements = async () => {
    try {
      setLoading(true);
      const response = await workshopApi.getWorkshopAnnouncements(workshopId, {
        limit: 20,
      });
      if (response.success) {
        setAnnouncements(response.data.announcements || []);
        // Store user permissions from API response to check canCreate
        if (response.data.userPermissions) {
          setUserPermissions(response.data.userPermissions);
        }
      }
    } catch (error) {
      setError(error.message);
} finally {
      setLoading(false);
    }
  };

  const handleCreateAnnouncement = async (announcementData) => {
    try {
      if (announcementData.priority === "urgent") {
        await workshopApi.sendUrgentAnnouncement(workshopId, announcementData);
      } else {
        await workshopApi.createAnnouncement(workshopId, announcementData);
      }
      setShowCreateForm(false);
      fetchAnnouncements(); // Refresh list
    } catch (error) {
alert("Failed to create announcement. Please try again.");
    }
  };

  if (loading) {
    return (
      <div className="wkd-announcements-container">
        <div className="wkd-announcements-header">
          <div className="wkd-announcements-title-section">
            <FiBell className="wkd-announcements-icon" />
            <div>
              <h2>Announcements</h2>
              <p className="wkd-announcements-subtitle">Loading...</p>
            </div>
          </div>
        </div>
        <div className="wkd-announcements-skeleton">
          {[1, 2, 3].map((i) => (
            <div key={i} className="wkd-skeleton-card">
              <div className="wkd-skeleton-priority-bar" />
              <div className="wkd-skeleton-body">
                <div className="wkd-skeleton-header">
                  <div className="wkd-skeleton-line wkd-skeleton-title" />
                  <div className="wkd-skeleton-badge" />
                </div>
                <div className="wkd-skeleton-content">
                  <div className="wkd-skeleton-line wkd-skeleton-text" />
                  <div className="wkd-skeleton-line wkd-skeleton-text" />
                  <div className="wkd-skeleton-line wkd-skeleton-text-short" />
                </div>
                <div className="wkd-skeleton-footer">
                  <div className="wkd-skeleton-author">
                    <div className="wkd-skeleton-avatar" />
                    <div className="wkd-skeleton-line wkd-skeleton-name" />
                  </div>
                  <div className="wkd-skeleton-line wkd-skeleton-date" />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    // Check if it's a 403 error or access denied for non-members
    const isAccessDenied =
      error.includes("403") ||
      error.includes("401") ||
      error.includes("503") ||
      error.includes("unauthorized") ||
      error.includes("not a participant") ||
      error.includes("API call failed with status 403");

    if (isAccessDenied || userRole === "non-member" || userRole === "visitor") {
      return (
        <div className="access-denied-state">
          <div className="access-denied-icon">🔒</div>
          <h3>Announcement Access Restricted</h3>
          <p>
            You need to be a workshop participant to view workshop
            announcements.
          </p>
          <div className="access-denied-actions">
            <button
              onClick={onNavigateToOverview}
              className="workshop-announcement-btn workshop-announcement-btn-primary"
            >
              View Workshop Details
            </button>
          </div>
        </div>
      );
    }

    return (
      <div className="error-state">
        <p>Failed to load announcements: {error}</p>
        <button onClick={fetchAnnouncements} className="retry-btn">
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="wkd-announcements-container">
      <div className="wkd-announcements-header">
        <div className="wkd-announcements-title-section">
          <FiBell className="wkd-announcements-icon" />
          <div>
            <h2>Announcements</h2>
            <p className="wkd-announcements-subtitle">
              {announcements.length} announcement{announcements.length !== 1 ? 's' : ''}
            </p>
          </div>
        </div>
        {canCreateAnnouncements && (
          <motion.button
            className="wkd-create-announcement-btn"
            onClick={() => setShowCreateForm(true)}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <FiPlus />
            New Announcement
          </motion.button>
        )}
      </div>

      <div className="wkd-announcements-list">
        {announcements.length === 0 ? (
          <motion.div 
            className="wkd-announcements-empty"
            variants={itemVariants}
            initial="initial"
            animate="animate"
          >
            <div className="wkd-empty-icon">
              <FiBell />
            </div>
            <h3>No Announcements Yet</h3>
            <p>
              {canCreateAnnouncements
                ? "Create your first announcement to keep participants informed about important updates."
                : "The organizer hasn't posted any announcements for this workshop yet."}
            </p>
            {canCreateAnnouncements && (
              <button 
                className="wkd-empty-action-btn"
                onClick={() => setShowCreateForm(true)}
              >
                <FiPlus /> Create First Announcement
              </button>
            )}
          </motion.div>
        ) : (
          <AnimatePresence>
            {announcements.map((announcement, index) => (
              <AnnouncementCard
                key={announcement._id}
                announcement={announcement}
                index={index}
              />
            ))}
          </AnimatePresence>
        )}
      </div>

      <AnimatePresence>
        {showCreateForm && (
          <AnnouncementCreateForm
            onSubmit={handleCreateAnnouncement}
            onCancel={() => setShowCreateForm(false)}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

const AnnouncementCard = ({ announcement, index }) => {
  const [expanded, setExpanded] = useState(false);
  const contentLength = announcement.content?.length || 0;
  const shouldTruncate = contentLength > 200;

  // Map backend fields to priority string for styling
  // Backend may send: isUrgent (boolean), isImportant (boolean), OR priority (string)
  const getPriority = () => {
    // Check for urgent first (from /announcements/urgent endpoint)
    if (announcement.isUrgent === true) return "urgent";
    if (announcement.priority === "urgent") return "urgent";
    // Then check for important
    if (announcement.isImportant === true) return "important";
    if (announcement.priority === "high" || announcement.priority === "important") return "important";
    // Check other priority values
    if (announcement.priority) return announcement.priority;
    // Default to normal
    return "normal";
  };

  const priority = getPriority();

  const getPriorityLabel = () => {
    const labels = {
      low: "Low",
      normal: "Normal",
      important: "Important",
      high: "Important",
      urgent: "Urgent",
    };
    return labels[priority] || "Normal";
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));

    if (diffDays > 7) {
      return date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: date.getFullYear() !== now.getFullYear() ? "numeric" : undefined,
      });
    }
    if (diffDays > 0) return `${diffDays}d ago`;
    if (diffHours > 0) return `${diffHours}h ago`;
    return "Just now";
  };

  return (
    <motion.div 
      className="wkd-announcement-card"
      variants={itemVariants}
      initial="initial"
      animate="animate"
      exit={{ opacity: 0, y: -10 }}
      layout
    >
      <div className={`wkd-announcement-priority-bar wkd-priority-${priority}`} />
      <div className="wkd-announcement-body">
        <div className="wkd-announcement-header">
          <h4 className="wkd-announcement-title">{announcement.title}</h4>
          <span className={`wkd-announcement-badge wkd-badge-${priority}`}>
            {getPriorityLabel()}
          </span>
        </div>
        <p className="wkd-announcement-text">
          {expanded || !shouldTruncate
            ? announcement.content
            : `${announcement.content?.substring(0, 200)}...`}
        </p>
        {shouldTruncate && (
          <button className="wkd-see-more-btn" onClick={() => setExpanded(!expanded)}>
            {expanded ? "Show less" : "Read more"}
          </button>
        )}
        {announcement.actionRequired && (
          <div className="wkd-action-required">
            <FiAlertCircle />
            <span>Action Required</span>
          </div>
        )}
        <div className="wkd-announcement-footer">
          <div className="wkd-announcement-author">
            {announcement.author?.profilePhotoUrl ? (
              <img 
                src={announcement.author.profilePhotoUrl} 
                alt={announcement.author.name}
                className="wkd-author-avatar"
              />
            ) : (
              <div className="wkd-author-avatar-placeholder">
                {announcement.author?.name?.[0]?.toUpperCase() || "?"}
              </div>
            )}
            <span>{announcement.author?.name || "Unknown"}</span>
          </div>
          <span className="wkd-announcement-date">
            {formatDate(announcement.createdAt)}
          </span>
        </div>
      </div>
    </motion.div>
  );
};

const AnnouncementCreateForm = ({ onSubmit, onCancel }) => {
  const [formData, setFormData] = useState({
    title: "",
    content: "",
    priority: "normal",
    sendEmail: true,
    sendNotification: true,
    actionRequired: false,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title.trim() || !formData.content.trim()) {
      return;
    }
    setIsSubmitting(true);
    try {
      await onSubmit(formData);
    } finally {
      setIsSubmitting(false);
    }
  };

  const priorityOptions = [
    { value: "low", label: "Low", description: "General updates" },
    { value: "normal", label: "Normal", description: "Standard announcements" },
    { value: "high", label: "Important", description: "Key information" },
    { value: "urgent", label: "Urgent", description: "Immediate attention needed" },
  ];

  return (
    <motion.div 
      className="wkd-modal-overlay" 
      onClick={onCancel}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <motion.div
        className="wkd-announcement-form-modal"
        onClick={(e) => e.stopPropagation()}
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 20 }}
        transition={{ type: "spring", damping: 25, stiffness: 300 }}
      >
        <div className="wkd-form-header">
          <div className="wkd-form-header-content">
            <FiBell className="wkd-form-icon" />
            <div>
              <h3>Create Announcement</h3>
              <p>Share important updates with workshop participants</p>
            </div>
          </div>
          <button className="wkd-form-close" onClick={onCancel} type="button">
            <FiX />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="wkd-form-body">
          <div className="wkd-form-group">
            <label>Title <span className="wkd-required">*</span></label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              required
              minLength={5}
              maxLength={200}
              placeholder="Enter a clear, concise title..."
              className="wkd-form-input"
            />
            <span className="wkd-char-count">{formData.title.length}/200</span>
          </div>

          <div className="wkd-form-group">
            <label>Content <span className="wkd-required">*</span></label>
            <textarea
              value={formData.content}
              onChange={(e) => setFormData({ ...formData, content: e.target.value })}
              required
              minLength={10}
              rows={5}
              maxLength={2000}
              placeholder="Write your announcement content here..."
              className="wkd-form-textarea"
            />
            <span className="wkd-char-count">{formData.content.length}/2000</span>
          </div>

          <div className="wkd-form-group">
            <label>Priority Level</label>
            <div className="wkd-priority-selector">
              {priorityOptions.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  className={`wkd-priority-option ${formData.priority === option.value ? 'active' : ''} wkd-priority-${option.value}`}
                  onClick={() => setFormData({ ...formData, priority: option.value })}
                >
                  <span className="wkd-priority-label">{option.label}</span>
                  <span className="wkd-priority-desc">{option.description}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="wkd-form-group">
            <label>Options</label>
            <div className="wkd-checkbox-group">
              <label className="wkd-checkbox-item">
                <input
                  type="checkbox"
                  checked={formData.sendEmail}
                  onChange={(e) => setFormData({ ...formData, sendEmail: e.target.checked })}
                />
                <span className="wkd-checkbox-custom"></span>
                <span className="wkd-checkbox-label">Send email notification</span>
              </label>
              <label className="wkd-checkbox-item">
                <input
                  type="checkbox"
                  checked={formData.sendNotification}
                  onChange={(e) => setFormData({ ...formData, sendNotification: e.target.checked })}
                />
                <span className="wkd-checkbox-custom"></span>
                <span className="wkd-checkbox-label">Send push notification</span>
              </label>
              <label className="wkd-checkbox-item">
                <input
                  type="checkbox"
                  checked={formData.actionRequired}
                  onChange={(e) => setFormData({ ...formData, actionRequired: e.target.checked })}
                />
                <span className="wkd-checkbox-custom"></span>
                <span className="wkd-checkbox-label">Mark as action required</span>
              </label>
            </div>
          </div>

          <div className="wkd-form-actions">
            <button
              type="button"
              className="wkd-btn wkd-btn-secondary"
              onClick={onCancel}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className={`wkd-btn wkd-btn-primary ${formData.priority === 'urgent' ? 'wkd-btn-urgent' : ''}`}
              disabled={isSubmitting || !formData.title.trim() || !formData.content.trim()}
            >
              {isSubmitting ? (
                <>
                  <FiLoader className="wkd-btn-spinner" />
                  Sending...
                </>
              ) : (
                <>
                  <FiSend />
                  {formData.priority === "urgent" ? "Send Urgent" : "Publish"}
                </>
              )}
            </button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
};

export default Announcements;
